import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StockDetailModal = ({ stock, isOpen, onClose, onAddToWatchlist }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !stock) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'financials', label: 'Financials', icon: 'DollarSign' },
    { id: 'news', label: 'News', icon: 'Newspaper' },
    { id: 'analysis', label: 'Analysis', icon: 'TrendingUp' }
  ];

  const mockFinancials = {
    revenue: '394.3B',
    netIncome: '99.8B',
    grossMargin: '43.3%',
    operatingMargin: '30.3%',
    eps: '6.16',
    bookValue: '4.40',
    debtToEquity: '1.73',
    roe: '175.0%'
  };

  const mockNews = [
    {
      id: 1,
      title: `${stock.name} Reports Strong Q4 Earnings`,
      summary: 'Company beats analyst expectations with record revenue growth and strong guidance for next quarter.',
      source: 'Financial Times',
      time: '2 hours ago',
      sentiment: 'positive'
    },
    {
      id: 2,
      title: `Analyst Upgrades ${stock.symbol} to Buy Rating`,
      summary: 'Major investment firm raises price target citing strong fundamentals and market position.',
      source: 'MarketWatch',
      time: '5 hours ago',
      sentiment: 'positive'
    },
    {
      id: 3,
      title: `${stock.name} Announces New Product Launch`,
      summary: 'Company unveils innovative technology solution expected to drive future growth.',
      source: 'Reuters',
      time: '1 day ago',
      sentiment: 'neutral'
    }
  ];

  const mockAnalysis = {
    analystRating: 'Buy',
    priceTarget: stock.price * 1.15,
    recommendations: {
      strongBuy: 8,
      buy: 12,
      hold: 5,
      sell: 1,
      strongSell: 0
    }
  };

  const handleAddToWatchlist = () => {
    onAddToWatchlist(stock);
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Price Chart Placeholder */}
      <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <Icon name="TrendingUp" size={48} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Interactive Price Chart</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Market Cap</p>
          <p className="text-lg font-semibold font-mono">{stock.marketCap}</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Volume</p>
          <p className="text-lg font-semibold font-mono">{stock.volume}</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">P/E Ratio</p>
          <p className="text-lg font-semibold font-mono">{stock.peRatio}</p>
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Dividend Yield</p>
          <p className="text-lg font-semibold font-mono">{stock.dividendYield}%</p>
        </div>
      </div>

      {/* Company Profile */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Company Profile</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Sector:</span>
            <span className="font-medium">{stock.sector}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Industry:</span>
            <span className="font-medium">Consumer Electronics</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Employees:</span>
            <span className="font-medium">164,000</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Founded:</span>
            <span className="font-medium">1976</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinancials = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Income Statement</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Revenue (TTM):</span>
              <span className="font-mono font-semibold">${mockFinancials.revenue}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Net Income:</span>
              <span className="font-mono font-semibold">${mockFinancials.netIncome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">EPS:</span>
              <span className="font-mono font-semibold">${mockFinancials.eps}</span>
            </div>
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Key Ratios</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gross Margin:</span>
              <span className="font-mono font-semibold">{mockFinancials.grossMargin}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Operating Margin:</span>
              <span className="font-mono font-semibold">{mockFinancials.operatingMargin}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">ROE:</span>
              <span className="font-mono font-semibold">{mockFinancials.roe}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNews = () => (
    <div className="space-y-4">
      {mockNews.map((article) => (
        <div key={article.id} className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-foreground pr-4">{article.title}</h4>
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              article.sentiment === 'positive' ? 'bg-success/20 text-success' :
              article.sentiment === 'negative'? 'bg-destructive/20 text-destructive' : 'bg-muted text-muted-foreground'
            }`}>
              {article.sentiment}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{article.summary}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{article.source}</span>
            <span>{article.time}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAnalysis = () => (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Analyst Recommendations</h3>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-success">{mockAnalysis.analystRating}</p>
            <p className="text-sm text-muted-foreground">Consensus Rating</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold font-mono">${mockAnalysis.priceTarget.toFixed(2)}</p>
            <p className="text-sm text-muted-foreground">Price Target</p>
          </div>
        </div>
        
        <div className="space-y-2">
          {Object.entries(mockAnalysis.recommendations).map(([rating, count]) => (
            <div key={rating} className="flex items-center justify-between">
              <span className="text-sm capitalize">{rating.replace(/([A-Z])/g, ' $1').trim()}:</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${(count / 26) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-mono w-6">{count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'financials':
        return renderFinancials();
      case 'news':
        return renderNews();
      case 'analysis':
        return renderAnalysis();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center overflow-hidden">
              <img
                src={stock.logo}
                alt={stock.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold" style={{ display: 'none' }}>
                {stock.symbol.charAt(0)}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{stock.symbol}</h2>
              <p className="text-muted-foreground">{stock.name}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-2xl font-bold font-mono text-foreground">
                ${stock.price.toFixed(2)}
              </div>
              <div className={`font-mono text-sm ${stock.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)} ({stock.change >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%)
              </div>
            </div>
            
            <Button variant="outline" onClick={handleAddToWatchlist}>
              <Icon name="Plus" size={16} className="mr-2" />
              Add to Watchlist
            </Button>
            
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default StockDetailModal;